/**
 * Date-of-birth utilities — single source for parsing + age + display
 * across every screen that touches a guest record.
 *
 * Strict, deterministic priority order (matches the spec rule "DO NOT
 * GUESS"):
 *
 *   PRIORITY 0 — Date instance: trusted as-is.
 *   PRIORITY 1 — YYYYMMDD raw 8-digit string (e.g. AAMVA DBB pre-norm).
 *                Year first; unambiguous.
 *   PRIORITY 2 — YYYY-MM-DD / YYYY/MM/DD ISO short form. Year first;
 *                unambiguous. Used by the AAMVA service post-fix and
 *                the Zebra parseLicenseDate post-fix.
 *   PRIORITY 3 — DD-MM-YYYY / DD/MM/YYYY back-compat for legacy DB
 *                rows written before the ISO migration. Day-first.
 *   PRIORITY 4 — ISO datetime (e.g. Mongo persisted Date). new Date()
 *                is unambiguous when the string contains 'T' or 'Z'.
 *
 * Anything else (locale-ambiguous "MM/DD/YYYY" without an explicit
 * year-first prefix, "SSO" sentinel, garbage) returns null and the
 * UI surfaces "DOB UNREADABLE — RESCAN ID". We never guess.
 *
 * Pure module — no React, no axios, no DI. Importable from anywhere.
 */
export const parseDob = (raw: any): Date | null => {
    if (raw == null) return null;

    // PRIORITY 0 — already a Date
    if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;

    if (typeof raw !== 'string') return null;
    const s = raw.trim();
    if (!s) return null;
    // Sentinels used by kupartybus auto-stub before complete-profile
    if (s === 'SSO' || s.toUpperCase() === 'UNKNOWN') return null;

    // PRIORITY 1 — YYYYMMDD raw 8-digit (defensive: never normally
    // passed in, but if AAMVA DBB ever leaks through unparsed we catch
    // it here instead of generic Date() locale-guessing).
    let m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
    if (m) {
        const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    // PRIORITY 2a — STRICT canonical ISO from server.
    // The /api/scanner/parse contract guarantees this exact shape;
    // short-circuit it without any fallback parsing. Any DOB that
    // fails this MUST come from a legacy DB row or non-server source.
    m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) {
        const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    // PRIORITY 2b — lenient ISO (slash separator, 1-digit month/day).
    // Back-compat ONLY for legacy DB rows; live scanner output never
    // takes this branch (covered by 2a). Year-first ⇒ unambiguous.
    m = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(s);
    if (m) {
        const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    // PRIORITY 3 — US driver-license / legacy formats with explicit
    // separators. Accepts BOTH dashes and slashes:
    //   MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY, M-D-YYYY  (preferred US/AAMVA)
    //   DD/MM/YYYY, DD-MM-YYYY                      (forced when first > 12)
    //
    // Updated 2026-05-09: previously rejected slashes outright and
    // defaulted dashed pairs to DD-MM. Real AAMVA driver licenses
    // present DOB in US MM-DD-YYYY format on screen and post-Phase-1
    // legacy DB rows use DD-MM with dashes — disambiguation handled
    // below. When neither number exceeds 12, we default to MM-DD
    // (the US driver-license convention) since that's the dominant
    // surface in this app.
    m = /^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/.exec(s);
    if (m) {
        const a = Number(m[1]);
        const b = Number(m[2]);
        const yy = Number(m[3]);
        // Forced — first number > 12 means it has to be a day.
        if (a > 12 && b <= 12) {
            const dt = new Date(yy, b - 1, a); // DD-(MM)-YYYY
            return Number.isNaN(dt.getTime()) ? null : dt;
        }
        // Forced — second number > 12 means first has to be a month.
        if (b > 12 && a <= 12) {
            const dt = new Date(yy, a - 1, b); // (MM)-DD-YYYY
            return Number.isNaN(dt.getTime()) ? null : dt;
        }
        // Ambiguous (both ≤ 12) — default to MM-DD-YYYY (US/AAMVA).
        const dt = new Date(yy, a - 1, b);
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    // PRIORITY 4 — ISO datetime (Mongo persisted: 2008-04-09T00:00:00Z).
    // Strip the time portion before constructing so a midnight-UTC
    // value doesn't shift to the previous day in CDT/CST.
    if (s.includes('T') || s.endsWith('Z')) {
        const dateOnly = s.split('T')[0];
        const m2 = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(dateOnly);
        if (m2) {
            const dt = new Date(
                Number(m2[1]),
                Number(m2[2]) - 1,
                Number(m2[3])
            );
            return Number.isNaN(dt.getTime()) ? null : dt;
        }
    }

    // Anything else (e.g. bare "04/09/2008" with no ISO prefix and
    // ambiguous month/day) — REFUSE TO GUESS. Return null so the UI
    // shows "DOB UNREADABLE — RESCAN ID" and staff verifies manually.
    return null;
};

/** Age in completed years from a parsed DOB. Returns null when DOB is null. */
export const ageFrom = (dob: Date | null): number | null => {
    if (!dob) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const md = today.getMonth() - dob.getMonth();
    if (md < 0 || (md === 0 && today.getDate() < dob.getDate())) age--;
    return age;
};

/** Format a Date as MM/DD/YYYY for display. Empty string for null. */
export const fmtMdy = (d: Date | null): string => {
    if (!d) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
};

/**
 * Format a Date as YYYY-MM-DD ISO short form. Used as the
 * `<input type="date">` default value so legacy DD-MM-YYYY records
 * pre-populate the picker correctly.
 */
export const fmtIso = (d: Date | null): string => {
    if (!d) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};
